import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../services/api';
import toast from 'react-hot-toast';

const emptyForm = { name: '', category: '', price: '' };

export default function Products() {
  const { selectedFactory } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const load = () => {
    if (!selectedFactory) return;
    setLoading(true);
    getProducts(selectedFactory.id, showInactive)
      .then(res => setProducts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selectedFactory, showInactive]);

  const openCreate = () => { setEditProduct(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (p) => { setEditProduct(p); setForm({ name: p.name, category: p.category || '', price: p.price }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Συμπληρώστε όνομα και τιμή');
    setSaving(true);
    try {
      if (editProduct) {
        await updateProduct(selectedFactory.id, editProduct.id, form);
        toast.success('Το προϊόν ενημερώθηκε');
      } else {
        await createProduct(selectedFactory.id, form);
        toast.success('Το προϊόν δημιουργήθηκε');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Σφάλμα');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (p) => {
    try {
      await updateProduct(selectedFactory.id, p.id, { is_active: !p.is_active });
      toast.success(p.is_active ? 'Απενεργοποιήθηκε' : 'Ενεργοποιήθηκε');
      load();
    } catch { toast.error('Σφάλμα'); }
  };

  const categories = [...new Set(products.map(p => p.category || 'Άλλα'))];

  return (
    <>
      <div className="page-header">
        <div className="flex-between">
          <div>
            <h2>Προϊόντα</h2>
            <p>Διαχείριση προϊόντων και τιμών — {selectedFactory?.name}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              Ανενεργά
            </label>
            <button className="btn btn-primary" onClick={openCreate}>+ Νέο Προϊόν</button>
          </div>
        </div>
      </div>
      <div className="page-body">
        {loading ? (
          <div className="page-loading"><span className="loading-spinner dark" /></div>
        ) : products.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">📦</div>
              <h3>Δεν υπάρχουν προϊόντα</h3>
              <p>Προσθέστε το πρώτο προϊόν</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openCreate}>+ Νέο Προϊόν</button>
            </div>
          </div>
        ) : (
          categories.map(cat => {
            const catProducts = products.filter(p => (p.category || 'Άλλα') === cat);
            return (
              <div key={cat} className="card" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <span className="card-title">{cat}</span>
                  <span className="badge badge-gray">{catProducts.length} προϊόντα</span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr><th>Όνομα</th><th>Τιμή</th><th>Κατάσταση</th><th style={{ textAlign: 'right' }}>Ενέργειες</th></tr>
                    </thead>
                    <tbody>
                      {catProducts.map(p => (
                        <tr key={p.id}>
                          <td><strong>{p.name}</strong></td>
                          <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(p.price).toFixed(2)}</strong></td>
                          <td>
                            <span className={`badge ${p.is_active ? 'badge-green' : 'badge-gray'}`}>
                              {p.is_active ? 'Ενεργό' : 'Ανενεργό'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                              <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>✏️ Επεξεργασία</button>
                              <button className={`btn btn-sm ${p.is_active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(p)}>
                                {p.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editProduct ? 'Επεξεργασία Προϊόντος' : 'Νέο Προϊόν'}</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Όνομα Προϊόντος *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="π.χ. Ψωμί Λευκό" />
              </div>
              <div className="form-group">
                <label className="form-label">Κατηγορία</label>
                <input className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="π.χ. Ψωμί, Γλυκά..." />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Τιμή (€) *</label>
                <input className="form-input" type="number" step="0.01" min="0" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Ακύρωση</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : (editProduct ? 'Αποθήκευση' : 'Δημιουργία')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
